import { isBefore, parse, startOfDay } from "date-fns";
import { Notifications } from "../../../store/store.types";
import { UserFromAPI } from "../../../types/auth.types";
import { GroupsOptions } from "../../../types/groups.types";
import { Session } from "../../../types/records.types";
import { FormatedFields } from "../../../types/fields.types";
import { NFB_SERVICE, PSY_SERVICE, ROLES, REPORTS_FIRST_SERVICE } from "../../../utils/constants";
import {
	ServiceProvided,
	ServicesProvidedForm,
	SessionsDates,
	ServiceProvidedWithItsSessions,
	ServicePrescribed,
} from "./types";
import { formatDate, getEndOfDayDate, isDateAfter, isDateAfterOrSameDay, isDateBefore, isDateBeforeOrSameDay } from "../../../utils/dateUtils";

export const SESSION_STATUS_TO_EXCLUDE: Array<Session["status"]> = ["missed", "cancelled", "staff_unavailable"];
export const SERVICES_PROVIDED_ENTITY = "services_provided"
export const treatmentPrescriptionFieldsQuery = [
	"id,date_created,date_updated,user_created.id,user_created.first_name,user_created.last_name,user_created.avatar.id,user_created.role,prescription_sent_date",
	"treatment.id,treatment.sessions.status,treatment.sessions.id,treatment.sessions.start_date,treatment.sessions.end_date,treatment.sessions.service.id,content",
	"treatment.client.id,treatment.client.user_id.first_name,treatment.client.user_id.last_name",
	"services_prescribed.*,services_prescribed.service.name,services_prescribed.service.id,services_prescribed.goals.*,services_prescribed.goals.treatment_therapy_goals_id.*",
	"services_provided.*,services_provided.user_created.first_name,services_provided.user_created.last_name,services_provided.user_created.avatar",
	"services_provided.service.id,services_provided.service.name",
	"services_provided.reserved_slots.*,services_provided.reserved_slots.therapist.*",
].join(",");

export const treatmentPrescriptionExtraOptions = {
	deep: {
		services_prescribed: {
			_sort: ["id"]
		}
	}
};

export const selectedServices = {
	nfb: { id: 33, label: "NeuroFeedback" },
	psy: { id: 34, label: "Psicologia" },
	qEEG: { id: 35, label: "Avaliação qEEG" },
};

export const getLabelByServiceId = (id: number): string | undefined => {
	return Object.values(selectedServices).find((service) => service.id === id)?.label;
};

export const getPeriodicityLabel = (periodicity: ServicesProvidedForm["session_periodicity"] | string, isPreviousPeriod = false) => {
	if (periodicity === "monthly") return { label: "Mensal", frequency: isPreviousPeriod ? "Mês Anterior" : "Neste Mês" };
	if (periodicity === "weekly") return { label: "Semanal", frequency: isPreviousPeriod ? "Semana Anterior" : "Nesta Semana" };
	if (periodicity === "not_applicable") return { label: "Não Aplicável", frequency: "Não Aplicável" };
	return { label: "", frequency: "" };
};

export const PRESCRIBED_SERVICES_MAX_NUMBER = Object.keys(selectedServices).length;
export const PROVIDED_SERVICES_MAX_NUMBER = [NFB_SERVICE, PSY_SERVICE].length;

// utilities for the Views
export const getColumnsNames = (formatedFields: FormatedFields, prefixHeaderNames?: Array<string>, sufixHeaderNames?: Array<string>, excludeFieldNames?: Array<string>) => {
	const excludeColumnsFromTable = [
		"id",
		"date_created",
		"date_updated",
		"user_updated",
		"user_created",
		"services_provided",
		"details",
		"service_provided",
		"initial_prescription",
		"treatment_prescription",
	];
	const specificExclusion = excludeFieldNames ?? []
	const columnNames = formatedFields
		.filter((field) => !excludeColumnsFromTable.includes(field.field))
		.filter(field => !specificExclusion.includes(field.field))
		.map((field) => field.headerName);
	const prefix = prefixHeaderNames ?? []
	const sufix = sufixHeaderNames ?? []
	return [...prefix, ...columnNames, ...sufix ];
};

export const displayEstimatedSessions = (sessions: number, serviceId: number): string => {
	if (!sessions) return "0"
	if (sessions <= 10) return sessions.toString();
	let sessionToAdd = serviceId === 33 ? 5 : 10
	return `${sessions} a ${sessions + sessionToAdd}`;
  };

export const lastestBookedValidationColor = (latestBookedSession: string | null | undefined): string => {
	if (!latestBookedSession) return "default";
	const parsedDate = parse(latestBookedSession, "dd/MM/yyyy", new Date());
	// Is valid if it is booked for today or in the future
	const isValid = !isBefore(startOfDay(parsedDate), startOfDay(new Date()));
	return isValid ? "default" : "red";
};

export const getSessionsOfServiceProvided = (
	sessions: Array<Session>,
	serviceProvided: ServiceProvided
): ServiceProvidedWithItsSessions => {

	const selectedSessions = sessions.filter((session) => {
		if (!serviceProvided.date_begin) return false;
		const isAfterBegin = isDateAfterOrSameDay(session.end_date, serviceProvided.date_begin);

		const isAfterEnd =
			serviceProvided.date_end !== undefined && isDateAfterOrSameDay(session.end_date, serviceProvided.date_end);

		const dateSelection = isAfterBegin && !isAfterEnd;
		const serviceSelection = session.service?.id === serviceProvided.service?.id;
		const selectionByStatus = !SESSION_STATUS_TO_EXCLUDE.includes(session.status);
		return dateSelection && serviceSelection && selectionByStatus;
	});

	return {
		serviceProvidedId: serviceProvided?.id,
		serviceId: serviceProvided.service?.id,
		sessions: selectedSessions
			.map((session) => ({
				service: session.service,
				status: session.status,
				start_date: session.start_date,
			})),
	};
};

export const countStaffUnavailableSessionsForService = (
	sessions: Array<Session>,
	serviceProvided: ServiceProvided
) => {
	const staffUnavailableSessions = sessions.filter((session) => {
		if (!serviceProvided.date_begin) return false;
		const isStaffUnavailable = session.status === "staff_unavailable";
		const isMatchingService = session.service?.id === serviceProvided.service?.id;
		const isAfterBegin = isDateAfterOrSameDay(session.end_date, serviceProvided.date_begin);
		const isAfterEnd =
			serviceProvided.date_end !== undefined && isDateAfterOrSameDay(session.end_date, serviceProvided.date_end);
		const isWithinDateRange = isAfterBegin && !isAfterEnd;
		return isStaffUnavailable && isMatchingService && isWithinDateRange;
	});
	return staffUnavailableSessions.length;
};

export const getSessionDatesOfServiceProvided = (serviceProvidedWithSessions: ServiceProvidedWithItsSessions) => {
	const sessionsDates: SessionsDates = {
		mostRecentDate: null,
		oldestDate: null,
		latestBookedSession: null,
	};

	const services = [PSY_SERVICE, NFB_SERVICE];
	const sessions = serviceProvidedWithSessions.sessions;
	let mostRecentDateValue: Date | null = null;
	let oldestDateValue: Date | null = null;
	let latestBookedValue: Date | null = null;

	sessions.forEach((session) => {

		const serviceId = typeof session.service === "object" ? session.service.id : session.service;
		if (!services.includes(serviceId)) return;
		const sessionDate = session.start_date ? new Date(session.start_date) : null;

		if (sessionDate) {
			if (session.status === "done") {
				if (!mostRecentDateValue || sessionDate > mostRecentDateValue) {
					mostRecentDateValue = sessionDate;
					sessionsDates.mostRecentDate = formatDate(session.start_date);
				}
				if (!oldestDateValue || sessionDate < oldestDateValue) {
					oldestDateValue = sessionDate;
					sessionsDates.oldestDate = formatDate(session.start_date);
				}
			}
			if (
				session.status === "booked" ||
				session.status === "confirmed" ||
				session.status === "awaiting_in_clinic"
			) {
				if (!latestBookedValue || sessionDate < latestBookedValue) {
					latestBookedValue = sessionDate;
					sessionsDates.latestBookedSession = formatDate(session.start_date);
				}
			}
		}
	});
	return sessionsDates;
};

// Form utilities - input
export const getGroupOptionsServicesPrescribed = (index: number, canEdit: boolean, servicesWithGoals?: Array<number>, currentServiceId?: number): GroupsOptions => {

	const shouldHideGoals = servicesWithGoals && currentServiceId ? !servicesWithGoals.includes(currentServiceId) : false;
	return {
		fieldsWithExtraOptions: [
			{
				field: `services_prescribed.${index}.estimated_total_sessions`,
				disabled: !canEdit,
			},
			{
				field: `services_prescribed.${index}.service`,
				relatedTable: "services",
				displayTemplate: "{{name}}",
				enabled: true,
				sort: "sort_order",
				filter: servicesOptionFilterPrescribed,
				disabled: !canEdit,
			},
		{
			field: `services_prescribed.${index}.goals`,
			relatedTable: "treatment_therapy_goals",
			displayTemplate: "{{goal}}",
			fieldsQuery: "*",
			enabled: true,
			sort: "id",
			isMultipleRelated: true,
			relatedWith: `services_prescribed.${index}.service`,
			createFilter: (selectedService: any) => {
				if (!selectedService?.id) return null;
				return {
					service: {
						_eq: selectedService.id,
					},
				};
			},
			dependency: {
				field: `services_prescribed.${index}.service`,
				placeHolder: "Por favor, selecione primeiro um serviço",
			},
			hidden: {
				create: shouldHideGoals,
				update: shouldHideGoals,
			},
			//disabled: !canEdit,
		},
		],
	};
};
export const getGroupOptionsServicesProvided = (canEdit?: boolean, isPeriodicityApplicable?: boolean): GroupsOptions => {
	const editable = canEdit ?? true
	const periodicityApplicable = isPeriodicityApplicable ?? true
	return {
		fieldsWithExtraOptions: [
			{
				field: "date_begin",
				disabled: !editable,
			},
						{
				field: "service",
				relatedTable: "services",
				displayTemplate: "{{name}}",
				enabled: true,
				disabled: !editable,
				sort: "sort_order",
				filter: servicesOptionFilterProvided,
			},
			{
				field: "session_quantity",
				disabled: !editable,
				hidden: {
					update: !periodicityApplicable,
					create: !periodicityApplicable,
				},
			},
			{
				field: "session_periodicity",
				disabled: !editable,
			},
		],
	};
};

const servicesOptionFilterPrescribed = {
	_and: [
		{
			id: {
				_in: Object.values(selectedServices).map((service) => service.id),
			},
		},
		{ sort_order: { _nnull: true } },
	],
};
const servicesOptionFilterProvided = {
	_and: [
		{
			id: {
				_in: Object.values(selectedServices)
					.filter((item) => ![50, 35].includes(item.id))
					.map((service) => service.id),
			},
		},
		{ sort_order: { _nnull: true } },
	],
};

export const canUserEditData = (
	data: { date_created?: string; date_updated?: string } | undefined,
	sessions: Array<Session> | undefined,
	currentUser: Pick<UserFromAPI, "id" | "first_name" | "last_name" | "role">
) => {
	const isAdmin = [ROLES.admin, ROLES.shilan, ROLES.technical_director].includes(currentUser.role.id);
	if (isAdmin) return true;

	// the edit lock only applies once the "Consulta de Avaliação" session is done; until then editing stays open
	const isEvaluationSessionDone = sessions?.some(
		(session) => session.service?.id === REPORTS_FIRST_SERVICE && session.status === "done"
	);
	if (!isEvaluationSessionDone) return true;

	if (!data) return true;
	const mostRecentChange = data.date_updated || data.date_created;
	const gracePeriod = 2 * 24 * 60 * 60 * 1000; // 2 days
	const isWithinGracePeriod = mostRecentChange ? Date.now() < new Date(mostRecentChange).getTime() + gracePeriod : false;
	return isWithinGracePeriod;
};

// Form utilities - customHooksFormHandler
export const validateIfServiceSelected = (formDataWithServices: any, notifications: Notifications) => {
	if (!formDataWithServices || formDataWithServices?.length <= 0) {
		notifications.setNotification({
			isOpen: true,
			message: "É necessário adicionar serviços para guardar.",
			severity: "warning",
		});
		return false;
	}
	return true;
};

export const validateDuplicatedServices = <T extends { service: { id: number } }>(
	formDataWithServices: Array<T>,
	notifications: Notifications
) => {
	const servicesIds = formDataWithServices.map((item: any) => item?.service?.id);
	const hasDuplicatedServices = new Set(servicesIds).size !== servicesIds.length;
	if (hasDuplicatedServices) {
		notifications.setNotification({
			isOpen: true,
			message: "Não pode haver serviços repetidos.",
			severity: "warning",
		});
		return false;
	}
	return true;
};

export const validateServiceAndGoalsMatch = (
	servicesPrescribed: Array<ServicePrescribed>,
	treatmentTherapyGoals: Array<any>,
	notifications: Notifications
) => {
	const serviceGoalsMap = new Map<number, Array<number>>();
	treatmentTherapyGoals?.forEach((goal: any) => {
		const serviceId = goal.service;
		const goalId = goal.id;

		const existingGoals = serviceGoalsMap.get(serviceId);
		if (existingGoals) {
			existingGoals.push(goalId);
		} else {
			serviceGoalsMap.set(serviceId, [goalId]);
		}
	});

	let hasCorrectServiceAndGoalsMatch = true;
	for (const servicePrescribed of servicesPrescribed) {
		const serviceId = servicePrescribed?.service?.id;
		const goalsIds = servicePrescribed.goals?.map((goal) => goal?.id ?? goal?.treatment_therapy_goals_id) ?? [];
		for (const goalId of goalsIds) {
			const validGoalsForService = serviceGoalsMap.get(serviceId);
			if (validGoalsForService && !validGoalsForService.includes(goalId)) {
				hasCorrectServiceAndGoalsMatch = false;
				break;
			}
		}
	}
	if (!hasCorrectServiceAndGoalsMatch) {
		notifications.setNotification({
			isOpen: true,
			message: "Os serviço selecionado não corresponde aos objetivos escolhidos.",
			severity: "warning",
		});
		return hasCorrectServiceAndGoalsMatch;
	}
	return hasCorrectServiceAndGoalsMatch;
};


export const customScrollbar = {
	// Custom scrollbar styling for minimalist design
	overflow: "auto",
	"&::-webkit-scrollbar": {
		height: "3px !important",
		width: "3px !important",
	},
	"&::-webkit-scrollbar-track": {
		backgroundColor: "transparent !important",
		borderRadius: "0px",
	},
	"&::-webkit-scrollbar-thumb": {
		backgroundColor: "rgba(0, 0, 0, 0.15) !important",
		borderRadius: "2px",
		border: "none",
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.25) !important",
		},
	},
	"&::-webkit-scrollbar-corner": {
		backgroundColor: "transparent",
	},
	// Firefox scrollbar styling
	scrollbarWidth: "thin",
	scrollbarColor: "rgba(0, 0, 0, 0.15) transparent",
};

export const activeServicesProvidedFilter = (serviceProvided: ServiceProvided) => {
	return (
		(!serviceProvided.date_end || isDateAfterOrSameDay(serviceProvided.date_end, new Date()))
		&&
		isDateBeforeOrSameDay(serviceProvided.date_begin, new Date())
	);
};

export const finishedServicesProvidedFilter = (serviceProvided: ServiceProvided) => {
	if (!serviceProvided.date_end) return false
    return (
        isDateBefore(getEndOfDayDate(serviceProvided.date_end), new Date())
    )
}

export const plannedServicesProvidedFilter = (serviceProvided: ServiceProvided) => {
	return (
		isDateAfter(serviceProvided.date_begin, new Date()))
}
